import { z } from 'zod';
import { ADJUSTMENT_NAMES, REAL_FEED_NAMES } from './marketdata.js';
import { chartBarSchema, chartDailyBarSchema, chartIndicatorRowSchema } from './chart.js';
import { setupTagSchema } from './labels.js';
/**
 * Replay labelling's read model (B4).
 *
 * This is a deliberately SMALLER shape than B1's chart response, and every omission is the
 * point. `ChartResponse` carries the session's open and close, the whole-session coverage
 * figure and a daily strip whose last entry is today's finished bar. Each of those is a fact
 * about a future the labeller has not reached, and sending one would destroy the measurement
 * this task exists to produce — invisibly, because a replay label made while the future was
 * visible looks exactly like an honest one.
 *
 * So there is no `sessions` array here, no `close`, no `sessionCoverage`. The client cannot
 * accidentally render what it was never sent, and a reviewer can check the guarantee by
 * reading this file rather than by auditing every consumer.
 *
 * The one figure that IS knowable at the cursor is `coverageSoFar` — minutes present over
 * minutes elapsed, open through the cursor bar. B0 already draws this distinction for the
 * indicator column of the same name; it is honoured here.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'date must be a real date');
const instant = z.string().datetime({ offset: true });
const symbol = z
    .string()
    .regex(/^[A-Z][A-Z0-9.]{0,9}$/, 'symbol must be 1–10 upper-case letters/digits/dots');
export const REPLAY_EXIT_REASONS = ['manual', 'stop_hard', 'stop_atr', 'window_end'];
export const replayExitReasonSchema = z.enum(REPLAY_EXIT_REASONS);
/**
 * The hard stop, as a fraction of the entry price, and the ATR multiple of the working stop.
 *
 * Both are the brief's figures and both are fixed rather than configurable. A stop the
 * labeller can loosen mid-trade is not a stop, and a replay pass whose stop distance varied
 * between sittings would not be comparable with itself.
 */
export const REPLAY_HARD_STOP_PCT = 0.07;
export const REPLAY_ATR_STOP_MULT = 1.5;
export const startReplaySessionSchema = z
    .object({
    symbol,
    sessionDate: isoDate,
    feed: z.enum(REAL_FEED_NAMES).default('iex'),
    adjustment: z.enum(ADJUSTMENT_NAMES).default('split'),
    /**
     * Present only when this deliberately replaces a live sitting. Required in that case:
     * "how many attempts did this session take, and why" is a fact about the labelling
     * process worth keeping, and it cannot be reconstructed later.
     */
    restartReason: z.string().min(3).max(500).optional(),
    dailyBars: z.number().int().min(0).max(250).default(60),
})
    .strict();
/** The live stop pair, priced at entry and frozen there. */
export const replayStopsSchema = z.object({
    hard: z.string(),
    /** Null when the entry bar has no `atr_14` — an honestly absent stop, not a fabricated one. */
    atr: z.string().nullable(),
    atrAtEntry: z.string().nullable(),
});
export const replayPositionSchema = z.object({
    id: z.string().uuid(),
    direction: z.enum(['long', 'short']),
    entryTs: instant,
    entryPrice: z.string(),
    decidedAt: instant,
    stops: replayStopsSchema,
    /** Mark-to-market at the cursor bar's close, so the readout is not a client computation. */
    unrealizedPct: z.string().nullable(),
});
/** A trade this sitting has already closed. Market instants only — no audit timestamps. */
export const replayFillSchema = z.object({
    id: z.string().uuid(),
    labelId: z.string().uuid().nullable(),
    direction: z.enum(['long', 'short']),
    entryTs: instant,
    entryPrice: z.string(),
    exitTs: instant,
    exitPrice: z.string(),
    exitReason: replayExitReasonSchema,
    returnPct: z.string(),
});
export const replaySessionSchema = z.object({
    id: z.string().uuid(),
    labelSessionId: z.string().uuid(),
    symbol: z.string(),
    sessionDate: isoDate,
    feed: z.enum(REAL_FEED_NAMES),
    adjustment: z.enum(ADJUSTMENT_NAMES),
    /** The last bar the labeller may see, inclusive. Server-authoritative; only ever increases. */
    cursorTs: instant,
    /** The session open. Safe: the cursor starts after it, so it is never in the future. */
    openTs: instant,
    /**
     * Whether the cursor has reached the end of the decision window. Deliberately a boolean and
     * not the window's last instant: "how much of the session is left" is a fact about a future
     * the labeller has not reached, and on an early-close day it would announce the early close.
     */
    finished: z.boolean(),
    /** Bars revealed so far, which the labeller can see anyway by counting them. */
    barsRevealed: z.number().int(),
    restartOf: z.string().uuid().nullable(),
});
export const replayChartSchema = z.object({
    session: replaySessionSchema,
    bars: z.array(chartBarSchema),
    /** Prior closed days, plus today-so-far built from the revealed bars. Never today's close. */
    daily: z.array(chartDailyBarSchema),
    indicators: z.array(chartIndicatorRowSchema),
    indicatorVersion: z.number().int().nullable(),
    /** Minutes present over minutes elapsed, open through the cursor. Point-in-time by design. */
    coverageSoFar: z.string(),
    position: replayPositionSchema.nullable(),
    fills: z.array(replayFillSchema),
});
/**
 * Advancing takes no cursor. The client says "next bar" and the server decides where that is —
 * the alternative, a client-supplied target, is the same hole as a client-supplied `until`
 * wearing a different name.
 */
export const advanceReplaySchema = z
    .object({ bars: z.number().int().min(1).max(78).default(1) })
    .strict();
export const openReplayPositionSchema = z
    .object({
    direction: z.enum(['long', 'short']),
    /**
     * Where in the cursor bar the fill happened. Omitted means the bar's close, which is what
     * `Space` then `E` means. A price outside the bar is refused by B2's trigger anyway.
     */
    price: z.string().optional(),
})
    .strict();
/**
 * Closing writes the label, so everything B2 needs that the position does not already know
 * arrives here. Confidence and tags are asked for at the exit rather than the entry because
 * at the entry they would be a guess about a trade that has not happened yet.
 */
export const closeReplayPositionSchema = z
    .object({
    price: z.string().optional(),
    confidence: z.number().int().min(1).max(5),
    setupTag: z.array(setupTagSchema).min(1).max(4),
    rationale: z.string().max(2000).optional(),
})
    .strict();
export const replayQueueEntrySchema = z.object({
    symbol: z.string(),
    sessionDate: isoDate,
    coverage: z.string().nullable(),
    /** Labels this labeller has already written on it, so a finished session is visibly done. */
    labelCount: z.number().int(),
    status: z.enum(['pending', 'in_progress', 'done']),
});
export const replayQueueSchema = z.object({
    entries: z.array(replayQueueEntrySchema),
    reserved: z.number().int(),
    pending: z.number().int(),
    done: z.number().int(),
});
//# sourceMappingURL=replay.js.map